import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { ArrowRight, Check, Gamepad2, Send } from 'lucide-react'
import { founderUrl } from './founderRoles'
import type { FounderRole } from './founderRoles'
import { postSignup } from './signup'

// Finished trailer: put the files in public/ and set both paths. Empty video = group photo.
const trailer = { video: '', poster: '' }

type Status = 'idle' | 'loading' | 'success' | 'error'

export default function BetaFooter({ role, preview }: { role: FounderRole; preview: boolean }) {
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [message, setMessage] = useState('')
  const sending = useRef(false)
  const shareText = `Certified ${role.label}. Just claimed my beta seat for LARPup: The Game. Only certified LARPers get in.`
  const shareUrl = founderUrl(role, 'x')
  const xIntent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`

  async function claimSeat(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (sending.current || status === 'success' || !email.trim()) return
    if (preview) {
      setStatus('success')
      setMessage('Preview only: this is the confirmation layout. No email was sent or saved.')
      return
    }
    sending.current = true
    setStatus('loading')
    try {
      await postSignup('beta', email.trim(), website)
      setStatus('success')
      setMessage("We'll email you when the Steam beta opens. Now recruit your co-founders.")
      setEmail('')
    } catch {
      setStatus('error')
      setMessage('We could not confirm your seat. Please try again.')
    } finally {
      sending.current = false
    }
  }

  return (
    <>
      <section className="bf" id="beta" aria-labelledby="beta-title">
        <div className="bf-stage">
          <figure className="bf-photo">
            {trailer.video
              ? <video src={trailer.video} poster={trailer.poster || undefined} controls playsInline preload="none" />
              : <img src="/sections/group-certified.webp" alt="" loading="lazy" decoding="async" />}
          </figure>
          <div className="bf-card">
            <span className="bf-kicker"><Gamepad2 size={16} aria-hidden="true" /> Coming to Steam</span>
            <h2 id="beta-title">Claim your seat.</h2>
            <p>LARPup is becoming a game. Only certified LARPers get into the beta. Your credential: <strong>{role.label}</strong>.</p>
            {status === 'success' ? (
              <div className="bf-done" role="status">
                <span className="bf-check"><Check size={20} /></span>
                <strong>{preview ? 'This is your seat-reserved state.' : `Seat reserved, ${role.label}.`}</strong>
                <p>{message}</p>
                <div className="bf-share">
                  <a href={xIntent} target="_blank" rel="noopener noreferrer">Share on X <ArrowRight size={14} /></a>
                  {'share' in navigator && <button type="button" onClick={() => navigator.share({ text: shareText, url: shareUrl }).catch(() => {})}><Send size={14} /> Send to a friend</button>}
                </div>
              </div>
            ) : (
              <>
                <form className="bf-form" onSubmit={claimSeat} aria-label="Claim a LARPup game beta seat" aria-busy={status === 'loading'}>
                  <label className="sr-only" htmlFor="beta-email">Your email address</label>
                  <input id="beta-email" name="email" type="email" required maxLength={254} autoComplete="email" placeholder="you@certified-larper.com" value={email} onChange={e => setEmail(e.target.value)} disabled={status === 'loading'} aria-describedby="beta-consent beta-error" />
                  <div className="honeypot" aria-hidden="true"><label htmlFor="beta-website">Leave this field empty</label><input id="beta-website" name="website" value={website} onChange={e => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" /></div>
                  <button disabled={status === 'loading'} type="submit">{status === 'loading' ? 'Claiming…' : 'Claim my seat'}</button>
                </form>
                <p id="beta-error" className="bf-error" role="status">{status === 'error' ? message : ''}</p>
              </>
            )}
            <p className="bf-consent" id="beta-consent">Beta invite and game launch news only. Unsubscribe anytime. <a href="/privacy.html">Privacy</a></p>
          </div>
        </div>
        <div className="bf-giant" aria-hidden="true">LARPUP</div>
      </section>
      <footer className="bf-foot">
        <span>© 2026 LARPup · Independent parody. Not affiliated with LinkedIn or any watch brand. No income promise: the card is a joke, free, and needs no signup.</span>
        <a href="/privacy.html">Privacy</a>
      </footer>
    </>
  )
}
